import { useState } from "react";
import { toast } from "sonner";
import { CalendarClockIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFijarTipoDeCambio } from "@/hooks/useValorizacion";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import { formatearFecha } from "@/lib/fechas";
import type { Hecho } from "@/lib/generacion";
import { formatearPrecio, fuenteDelTipoDeCambio, hoyEnChile, type Valorizacion } from "@/lib/valorizacion";

interface TipoDeCambioDelInformeProps {
  informeId: number;
  tipoCambio: Valorizacion["tipo_cambio"];
  /** La hoja de hechos: ahí vive la fuente de cada valor. Puede no haber cargado todavía. */
  hechos: Hecho[];
}

const NOMBRE_DEL_INDICADOR = { uf: "la UF", usd: "el dólar" } as const;

/**
 * La fecha de valorización y el valor de la UF y del dólar de ese día.
 *
 * Los totales en pesos y dólares dependen de esto: sin tipo de cambio la
 * valorización se queda en UF. Los valores los resuelve el servidor contra la
 * CMF y mindicador; acá solo viaja la fecha, y un valor que no se pudo
 * conseguir se DICE en vez de dejarlo en blanco o suponerlo.
 */
export function TipoDeCambioDelInforme({ informeId, tipoCambio, hechos }: TipoDeCambioDelInformeProps) {
  const fijar = useFijarTipoDeCambio(informeId);
  const [editando, setEditando] = useState(false);
  const [fecha, setFecha] = useState("");
  const [sinResolver, setSinResolver] = useState<("uf" | "usd")[]>([]);
  const [errorDeFecha, setErrorDeFecha] = useState<string | null>(null);

  const fijado = tipoCambio.fecha_valorizacion !== null;
  const mostrandoFormulario = !fijado || editando;

  function enviar() {
    setErrorDeFecha(null);
    setSinResolver([]);

    fijar.mutate(fecha, {
      onSuccess: (resultado) => {
        setSinResolver(resultado.pendientes);
        setEditando(false);
        setFecha("");
        if (resultado.pendientes.length === 0) toast.success("Tipo de cambio fijado");
      },
      onError: (error) => {
        const campos = parseValidationErrors(error);
        const mensaje = campos?.fecha ?? getApiErrorMessage(error, "No pudimos fijar el tipo de cambio. Inténtalo de nuevo.");
        setErrorDeFecha(mensaje);
      },
    });
  }

  return (
    <section aria-label="Tipo de cambio" className="bg-card flex flex-col gap-3 rounded-xl border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <CalendarClockIcon aria-hidden className="text-ink-muted size-4" />
        <span className="text-label text-ink font-bold">Tipo de cambio</span>
        {fijado ? (
          <span className="text-label text-ink-body" data-testid="fecha-de-valorizacion">
            Valorización al {formatearFecha(tipoCambio.fecha_valorizacion!)}
          </span>
        ) : (
          <span className="text-label text-warning-soft-foreground font-bold">Sin tipo de cambio</span>
        )}
        {fijado && !editando ? (
          <Button type="button" size="sm" variant="ghost" className="ml-auto" onClick={() => setEditando(true)}>
            Cambiar la fecha
          </Button>
        ) : null}
      </div>

      {fijado ? (
        <dl className="grid gap-3 sm:grid-cols-2">
          <Indicador
            etiqueta="UF"
            nombre="la UF"
            valor={tipoCambio.uf}
            fuente={fuenteDelTipoDeCambio(hechos, "uf")}
          />
          <Indicador
            etiqueta="US$"
            nombre="el dólar"
            valor={tipoCambio.usd}
            fuente={fuenteDelTipoDeCambio(hechos, "usd")}
          />
        </dl>
      ) : (
        <p className="text-label text-ink-body max-w-prose text-pretty">
          Fija la fecha de valorización para convertir los totales a pesos y dólares. Mientras tanto la valorización
          queda solo en UF.
        </p>
      )}

      {sinResolver.length > 0 ? (
        <p role="status" className="text-label text-warning-soft-foreground bg-warning-soft rounded-md px-3 py-2">
          No pudimos obtener {sinResolver.map((i) => NOMBRE_DEL_INDICADOR[i]).join(" ni ")} de esa fecha. Lo demás quedó
          fijado: inténtalo con otra fecha o vuelve a intentarlo más tarde.
        </p>
      ) : null}

      {mostrandoFormulario ? (
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(evento) => {
            evento.preventDefault();
            if (fecha !== "") enviar();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fecha-de-valorizacion">Fecha de valorización</Label>
            <Input
              id="fecha-de-valorizacion"
              type="date"
              value={fecha}
              max={hoyEnChile()}
              aria-invalid={errorDeFecha !== null}
              onChange={(evento) => setFecha(evento.target.value)}
              className="w-48"
            />
          </div>
          <Button type="submit" disabled={fecha === "" || fijar.isPending}>
            {fijar.isPending ? "Fijando…" : "Fijar tipo de cambio"}
          </Button>
          {editando ? (
            <Button type="button" variant="ghost" onClick={() => setEditando(false)}>
              Cancelar
            </Button>
          ) : null}
          {errorDeFecha ? (
            <p role="alert" className="text-label text-danger-soft-foreground w-full">
              {errorDeFecha}
            </p>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}

function Indicador({
  etiqueta,
  nombre,
  valor,
  fuente,
}: {
  etiqueta: string;
  nombre: string;
  valor: number | null;
  fuente: ReturnType<typeof fuenteDelTipoDeCambio>;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-caption text-ink-hint font-bold uppercase">{etiqueta}</dt>
      {valor === null ? (
        <dd className="text-label text-warning-soft-foreground font-bold">
          {nombre.charAt(0).toUpperCase() + nombre.slice(1)} no se pudo obtener
        </dd>
      ) : (
        <>
          <dd className="text-h3 text-ink font-mono font-bold">{formatearPrecio(valor, "CLP")}</dd>
          {fuente ? <dd className="text-caption text-ink-muted">{fuente.texto}</dd> : null}
          {fuente?.fechaEfectiva ? (
            <dd className="text-caption text-ink-muted">
              Se usó el último día publicado: {fuente.fechaEfectiva}
            </dd>
          ) : null}
        </>
      )}
    </div>
  );
}
